/**
 * Service for interacting with "Me" (current user) endpoints.
 *
 * Responsibilities:
 * - Fetch and update current user profile/settings.
 * - Manage following/followers data.
 * - Fetch analytics and liked gif ids.
 * - Fetch/search current user's gifs/content.
 */
import type {
  IAnalyticsResponse,
  IFollowersListResponse,
  IGifList,
  IMeGifsResponse,
  IUser,
  IUserSearchResults,
  IUserSettings,
  IOperation,
  IUserGifsParams
} from "../types";

import type { ApiClient } from "@/common/api/http/types";

export default class MeService {
  public constructor(private readonly client: ApiClient) {}

  async getMe(): Promise<IUser> {
    return await this.client.get("v1/me", {
      "X-Session-Id": await this.client.getSessionId(),
    });
  }

  async updateProfileInfo(operations: IOperation[]): Promise<void> {
    return await this.client.patch("v1/me", { operations });
  }

  async deleteMyAccount() {
    return await this.client.delete("v1/me");
  }

  async fetchUserFollowers(props: { count?: number }): Promise<IFollowersListResponse> {
    const params = new URLSearchParams();

    if (props.count) {
      params.append("count", props.count.toString());
    }

    return await this.client.get(`v1/me/followers/populated?${params.toString()}`);
  }

  async getFollowingIds(): Promise<string[]> {
    return await this.client.get("v1/me/follows");
  }

  async getFollowing(props: { count: number; page: number }): Promise<IUserSearchResults> {
    const params = new URLSearchParams();

    params.append("count", props.count.toString());
    params.append("page", props.page.toString());

    return await this.client.get(`v2/me/following?${params.toString()}`);
  }

  async getFollowers(props: { count: number; page: number }): Promise<IUserSearchResults> {
    const params = new URLSearchParams();

    params.append("count", props.count.toString());
    params.append("page", props.page.toString());

    return await this.client.get(`v2/me/followers?${params.toString()}`);
  }

  async updateUserSettings(params: IUserSettings): Promise<void> {
    return await this.client.patch("v2/me/settings", params);
  }

  async getAnalytics(): Promise<IAnalyticsResponse> {
    return await this.client.get("v2/analytics");
  }

  async getLikedGifs(): Promise<string[]> {
    return await this.client.get("v2/likes");
  }

  async fetchUserGifs({ type, order, isPublic, count, page, tags }: IUserGifsParams): Promise<IMeGifsResponse> {
    const URLParams = new URLSearchParams({ type });

    if (order !== null) {
      URLParams.append("order", order);
    }

    if (isPublic !== null) {
      URLParams.append("public", isPublic === "1" ? "yes" : "no");
    }

    if (count !== null) {
      URLParams.append("count", count);
    }

    if (page !== null) {
      URLParams.append("page", page.toString());
    }

    if (tags && tags.length > 0) {
      URLParams.append("tags", tags.join(","));
    }

    return await this.client.get(`v2/me/content?${URLParams.toString()}`);
  }

  async searchMyGifs(params: {
    order: string;
    count: number;
    type?: string;
    page?: number;
    tags?: string;
  }): Promise<IGifList> {
    const query = new URLSearchParams();

    if (params.order) {
      query.append("order", params.order);
    }

    if (params.count) {
      query.append("count", params.count.toString());
    }

    if (params.type) {
      query.append("type", params.type);
    }

    if (params.page) {
      query.append("page", params.page.toString());
    }

    if (params.tags) {
      query.append("tags", params.tags);
    }

    const queryString = query.toString();
    return await this.client.get(`v2/me/content?${queryString}`);
  }
}
